import * as THREE from 'three';
import { InputManager } from './InputManager';
import { AudioManager } from './AudioManager';
import { World } from '../world/World';
import { PlayerController } from '../player/PlayerController';
import { InteractionSystem } from '../interaction/InteractionSystem';
import { Inventory } from '../inventory/Inventory';
import { PuzzleState } from '../puzzles/PuzzleState';
import { UIManager } from '../ui/UIManager';
import { PUZZLES, type ItemId } from '../config/gameConfig';

type Mode = 'loading' | 'title' | 'playing' | 'paused' | 'examine' | 'clear';

export class Game {
  private renderer: THREE.WebGLRenderer;
  private input: InputManager;
  private audio = new AudioManager();
  private ui = new UIManager();
  private world!: World;
  private player!: PlayerController;
  private interaction!: InteractionSystem;
  private inventory = new Inventory();
  private puzzles = new PuzzleState();

  private mode: Mode = 'loading';
  private examineKind: string | null = null;
  private clockHour = 12;
  private clockMinute = 0;
  private safeInput = '';
  private exitInput = '';
  private playTime = 0;
  private openDoors = new Set<string>();
  private raf = 0;
  private lastT = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.25;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.input = new InputManager(canvas);
    this.bindUI();
    window.addEventListener('resize', this.onResize);
  }

  getDebugApi() {
    return {
      teleport: (x: number, z: number, yaw = 0) => {
        this.player.setSpawn(x, z, yaw);
      },
      setMode: (m: Mode) => {
        this.mode = m;
      },
      getMode: () => this.mode,
      inventory: this.inventory,
      puzzles: this.puzzles,
      interact: (use = false) => this.tryInteract(use),
      give: (id: ItemId) => this.giveItem(id),
      openById: (id: string) => {
        const t = this.interaction.get(id);
        if (t) {
          this.interaction.current = t;
          this.tryInteract(false);
        }
      },
    };
  }

  async start(): Promise<void> {
    this.ui.showScreen('loading');
    this.ui.setLoadProgress(0.1, 'ワールドを構築中…');
    await this.delay(50);

    this.world = new World(this.renderer);
    this.ui.setLoadProgress(0.45, 'プレイヤー初期化…');
    await this.delay(30);

    this.player = new PlayerController(window.innerWidth / window.innerHeight);
    this.player.colliders = this.world.build.colliders;
    this.player.setSpawn(0, 1.2, 0);

    this.interaction = new InteractionSystem(this.world.build.interactables);
    this.world.setupPostprocessing(this.player.camera);

    this.ui.setLoadProgress(0.75, 'UI 準備…');
    this.ui.setupMobile(this.input);
    this.inventory.onChange = () => this.ui.refreshInventory(this.inventory);
    this.puzzles.onFlag = () => this.refreshObjectives();
    this.ui.refreshInventory(this.inventory);
    this.refreshObjectives();

    this.ui.setLoadProgress(1, '完了');
    await this.delay(200);
    this.mode = 'title';
    this.ui.showScreen('title');

    this.lastT = performance.now();
    this.raf = requestAnimationFrame(this.loop);
  }

  private bindUI(): void {
    this.ui.onStart = () => void this.beginPlay();
    this.ui.onResume = () => this.resume();
    this.ui.onToTitle = () => this.toTitle();
    this.ui.onReplay = () => window.location.reload();
    this.ui.onVolumeBgm = (v) => this.audio.setBgmVolume(v);
    this.ui.onVolumeSfx = (v) => this.audio.setSfxVolume(v);
    this.ui.onSensitivity = (v) => {
      this.input.sensitivity = v;
    };
    this.ui.onToggleHints = () => this.ui.setHint(this.puzzles.getHint());
    this.ui.onExamineClose = () => this.closeExamine();
    this.ui.onSelectItem = (id) => {
      this.inventory.select(id);
      this.ui.refreshInventory(this.inventory);
      this.audio.playClick();
    };
    this.ui.onExamineAction = (action, payload) => this.handleExamineAction(action, payload);
  }

  private async beginPlay(): Promise<void> {
    await this.audio.unlock();
    this.audio.startAmbience();
    this.mode = 'playing';
    this.ui.showScreen('playing');
    this.playTime = 0;
    this.input.requestPointerLock();
    this.ui.toast('館を探索しよう。E で調べる · F でアイテム使用');
  }

  private resume(): void {
    this.mode = 'playing';
    this.ui.showScreen('playing');
    this.input.requestPointerLock();
  }

  private pause(): void {
    if (this.mode !== 'playing') return;
    this.mode = 'paused';
    this.input.exitPointerLock();
    this.ui.showScreen('paused');
  }

  private toTitle(): void {
    this.input.exitPointerLock();
    this.mode = 'title';
    this.ui.showScreen('title');
  }

  private closeExamine(): void {
    this.mode = 'playing';
    this.examineKind = null;
    this.ui.showScreen('playing');
    this.input.requestPointerLock();
  }

  private refreshObjectives(): void {
    this.ui.setObjective(this.puzzles.getObjective());
    this.ui.setHint(this.puzzles.getHint());
  }

  private loop = (t: number): void => {
    this.raf = requestAnimationFrame(this.loop);
    const dt = Math.min(0.05, (t - this.lastT) / 1000);
    this.lastT = t;
    this.update(dt);
    this.world.render(this.player.camera);
  };

  private update(dt: number): void {
    this.ui.update(dt);

    if (this.mode === 'playing') {
      this.playTime += dt;
      this.player.enabled = true;
      this.player.update(dt, this.input, () => this.audio.playFootstep());

      const target = this.interaction.update(this.player);
      if (target) this.ui.setPrompt(target.prompt ?? 'E 調べる', true);
      else this.ui.setPrompt(null);

      if (this.input.interactPressed) this.tryInteract(false);
      if (this.input.usePressed) this.tryInteract(true);
      if (this.input.pausePressed) this.pause();
      if (this.input.hintPressed) {
        this.ui.hintsEnabled = !this.ui.hintsEnabled;
        this.ui.updateHintVisibility();
        this.ui.toast(this.ui.hintsEnabled ? 'ヒント表示 ON' : 'ヒント表示 OFF');
      }
      if (this.input.inventoryPressed) {
        this.inventory.cycle();
        this.ui.refreshInventory(this.inventory);
      }
    } else {
      this.player.enabled = false;
      if (this.mode === 'examine' && (this.input.pausePressed || this.input.interactPressed)) {
        this.closeExamine();
      }
      if (this.mode === 'paused' && this.input.pausePressed) this.resume();
    }

    this.input.endFrame();
  }

  private tryInteract(useMode: boolean): void {
    const target = this.interaction.current;
    if (!target) return;

    switch (target.kind) {
      case 'examine':
        this.openExamineText(target.title, target.body ?? '');
        if (target.id === 'painting') {
          this.puzzles.set('saw_painting');
          this.refreshObjectives();
        }
        if (target.id === 'emblem' && this.puzzles.has('mirrors_done')) {
          this.openExamineText(
            target.title,
            '紋章が黄金に輝き、数字が浮かび上がっている——\n\n【 7 2 9 】\n\nこれが玄関の鍵だ。',
          );
        }
        break;
      case 'sheet_music':
        this.openExamineText(target.title, target.body ?? '');
        break;
      case 'clock':
        this.openClockUI();
        break;
      case 'drawer':
        this.handleLivingDrawer();
        break;
      case 'fireplace':
        this.handleFireplace(useMode);
        break;
      case 'bookshelf':
        this.openBookshelfUI();
        break;
      case 'safe':
        this.openSafeUI();
        break;
      case 'piano':
        this.openPianoUI();
        break;
      case 'cabinet':
        this.handleCabinet(useMode);
        break;
      case 'valves':
        this.openValvesUI();
        break;
      case 'use_item':
      case 'door':
        this.handleLocked(target, useMode);
        break;
      case 'pressure':
        this.openPressureUI();
        break;
      case 'fusebox':
      case 'wires':
        this.openFuseboxUI();
        break;
      case 'brazier':
        this.handleBrazier(useMode);
        break;
      case 'mirrors':
        this.openMirrorsUI();
        break;
      case 'exit':
        this.openExitUI();
        break;
      default:
        this.openExamineText(target.title, target.body ?? '');
    }
  }

  private openExamineText(title: string, body: string): void {
    this.mode = 'examine';
    this.examineKind = 'text';
    this.input.exitPointerLock();
    this.audio.playClick();
    this.ui.openExamine(title, body);
  }

  // —— Clock ——
  private openClockUI(): void {
    this.mode = 'examine';
    this.examineKind = 'clock';
    this.input.exitPointerLock();
    this.audio.playClick();
    this.renderClockUI();
  }

  private renderClockUI(): void {
    const hh = this.clockHour.toString().padStart(2, '0');
    const mm = this.clockMinute.toString().padStart(2, '0');
    const done = this.puzzles.has('clock_done');
    this.ui.openExamine(
      '置時計',
      done
        ? `針は ${hh}:${mm} を指している。サイドボードから音がした。`
        : `現在の設定: ${hh}:${mm}\n絵画のローマ数字を思い出して。`,
      done
        ? ''
        : `<div class="display-code">${hh}:${mm}</div>
        <div class="row-btns">
          <button class="clock-btn" data-action="clock" data-payload="h-">時 −</button>
          <button class="clock-btn" data-action="clock" data-payload="h+">時 ＋</button>
          <button class="clock-btn" data-action="clock" data-payload="m-">分 −</button>
          <button class="clock-btn" data-action="clock" data-payload="m+">分 ＋</button>
        </div>
        <button class="btn primary" data-action="clock" data-payload="set">この時刻に合わせる</button>`,
    );
  }

  // —— Fireplace ——
  private handleFireplace(_useMode: boolean): void {
    if (this.puzzles.has('paper_revealed')) {
      this.openExamineText('暖炉', '羊皮紙を炙ったあとだ。炎は静かに燃えている。');
      return;
    }
    if (!this.inventory.has('blank_paper')) {
      this.openExamineText(
        '暖炉',
        '炎が静かに揺れている。\n熱で文字が浮かぶ紙があるなら、ここで炙れるかもしれない。\n（白紙の羊皮紙を持って E / F）',
      );
      return;
    }
    this.inventory.remove('blank_paper');
    this.giveItem('revealed_note');
    this.puzzles.set('paper_revealed');
    this.audio.playFire();
    this.audio.playSuccess();
    this.refreshObjectives();
    this.ui.openExamine(
      '暖炉',
      '羊皮紙を炎にかざすと、薄茶色の文字が浮かび上がった。\n\n「本棚は 赤 → 青 → 緑 → 黄」\n\n（メモをインベントリに加えた）',
    );
    this.mode = 'examine';
    this.input.exitPointerLock();
  }

  // —— Bookshelf ——
  private openBookshelfUI(): void {
    this.mode = 'examine';
    this.examineKind = 'bookshelf';
    this.input.exitPointerLock();
    this.audio.playClick();
    this.renderBookshelfUI();
  }

  private renderBookshelfUI(): void {
    const labels = PUZZLES.shelfLabels;
    const done = this.puzzles.has('shelf_done');
    const order = this.puzzles.shelf
      .map((c) => labels[c as keyof typeof labels])
      .join(' · ');
    const btns = this.puzzles.shelf
      .map(
        (c, i) =>
          `<button class="valve-btn" data-action="shelf" data-payload="${i}">${i + 1}. ${labels[c as keyof typeof labels]} （右と入れ替え）</button>`,
      )
      .join('');
    this.ui.openExamine(
      '本棚',
      done
        ? `並びは整っている: ${order}\n金庫の封印が緩んだ気がする。`
        : `現在の並び: ${order}\n隣の本と入れ替えて正しい色順にしよう。\n（ヒント: 炙ったメモ）`,
      done
        ? ''
        : `<div class="row-btns" style="flex-direction:column">${btns}</div>
        <button class="btn primary" data-action="shelf" data-payload="check">並びを確定</button>
        <button class="btn ghost" data-action="shelf" data-payload="reset">初期配置に戻す</button>`,
    );
  }

  // —— Safe ——
  private openSafeUI(): void {
    if (this.puzzles.has('safe_opened')) {
      this.openExamineText('金庫', 'すでに開いている。中は空だ。');
      return;
    }
    if (!this.puzzles.has('shelf_done')) {
      this.openExamineText(
        '金庫',
        'テンキーが点かない。\n地球儀の刻印どおり、《言葉》（本棚の並び）を先に整えよ、ということか。',
      );
      return;
    }
    this.mode = 'examine';
    this.examineKind = 'safe';
    this.safeInput = '';
    this.input.exitPointerLock();
    this.audio.playClick();
    this.renderSafeUI();
  }

  private renderSafeUI(): void {
    const keys = '123456789C0✓';
    const pad = keys
      .split('')
      .map((k) => {
        const payload = k === '✓' ? 'ok' : k === 'C' ? 'clear' : k;
        return `<button data-action="safe" data-payload="${payload}">${k}</button>`;
      })
      .join('');
    this.ui.openExamine(
      '金庫',
      '4桁のパスコードを入力せよ。\n（絵画の数字 + 時計の分）',
      `<div class="display-code">${(this.safeInput + '____').slice(0, 4)}</div><div class="numpad">${pad}</div>`,
    );
  }

  // —— Piano ——
  private openPianoUI(): void {
    if (this.puzzles.has('piano_done')) {
      this.openExamineText('ピアノ', '旋律は正しく奏でられた。鍵盤下の小箱は空だ。');
      return;
    }
    this.mode = 'examine';
    this.examineKind = 'piano';
    this.puzzles.pianoBuffer = [];
    this.input.exitPointerLock();
    this.audio.playClick();
    this.renderPianoUI();
  }

  private renderPianoUI(): void {
    const notes = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
    const names: Record<string, string> = {
      C: 'ド',
      D: 'レ',
      E: 'ミ',
      F: 'ファ',
      G: 'ソ',
      A: 'ラ',
      B: 'シ',
    };
    const buf = this.puzzles.pianoBuffer.map((n) => names[n] ?? n).join(' → ') || '（未演奏）';
    const btns = notes
      .map((n) => `<button class="clock-btn" data-action="piano" data-payload="${n}">${names[n]}</button>`)
      .join('');
    this.ui.openExamine(
      'ピアノ',
      `演奏: ${buf}\nダイニングの楽譜を思い出して。`,
      `<div class="row-btns">${btns}</div>
      <button class="btn ghost" data-action="piano" data-payload="reset">やり直す</button>`,
    );
  }

  // —— Cabinet ——
  private handleCabinet(useMode: boolean): void {
    if (this.puzzles.has('cabinet_opened')) {
      this.openExamineText('キッチンの戸棚', 'すでに開けた。中は空だ。');
      return;
    }
    if (!this.inventory.has('silver_key')) {
      this.openExamineText('キッチンの戸棚', '細い鍵穴がある。銀の鍵が必要だ。');
      return;
    }
    void useMode;
    this.puzzles.set('cabinet_opened');
    this.world.openCabinet();
    this.audio.playDrawer();
    this.giveItem('wiring_diagram');
    this.refreshObjectives();
    this.ui.openExamine(
      'キッチンの戸棚',
      '銀の鍵が合った。\n中から配電盤の配線図を見つけた。\n「青→黄→赤の順に結線せよ」',
    );
    this.mode = 'examine';
    this.input.exitPointerLock();
  }

  // —— Valves ——
  private openValvesUI(): void {
    if (this.puzzles.has('valves_done')) {
      this.openExamineText('バルブ', '正しい順序で開けた。ランタンに油が満たされた。');
      return;
    }
    this.mode = 'examine';
    this.examineKind = 'valves';
    this.puzzles.valveSequence = [];
    this.input.exitPointerLock();
    this.audio.playClick();
    this.renderValvesUI();
  }

  private renderValvesUI(): void {
    const seq = this.puzzles.valveSequence
      .map((v) => ({ water: '水', oil: '油', gas: 'ガス' })[v] ?? v)
      .join(' → ') || '（未操作）';
    this.ui.openExamine(
      'キッチンのバルブ',
      `操作順: ${seq}\n冷蔵庫のメモを思い出そう。`,
      `<div class="row-btns">
        <button class="valve-btn" data-action="valve" data-payload="water">青（水）</button>
        <button class="valve-btn" data-action="valve" data-payload="oil">黄（油）</button>
        <button class="valve-btn" data-action="valve" data-payload="gas">赤（ガス）</button>
      </div>
      <button class="btn ghost" data-action="valve" data-payload="reset">リセット</button>`,
    );
  }

  // —— Pressure ——
  private openPressureUI(): void {
    if (this.puzzles.has('pressure_ok')) {
      this.openExamineText('圧力計', `針は ${this.puzzles.pressure} を示している。適正値だ。`);
      return;
    }
    this.mode = 'examine';
    this.examineKind = 'pressure';
    this.input.exitPointerLock();
    this.audio.playClick();
    this.renderPressureUI();
  }

  private renderPressureUI(): void {
    this.ui.openExamine(
      '圧力計',
      `現在値: ${this.puzzles.pressure}\n楽譜の題号がヒントだ。\n（0〜12）`,
      `<div class="display-code">${this.puzzles.pressure}</div>
      <div class="row-btns">
        <button class="clock-btn" data-action="pressure" data-payload="-">−</button>
        <button class="clock-btn" data-action="pressure" data-payload="+">＋</button>
      </div>
      <button class="btn primary" data-action="pressure" data-payload="set">この値で確定</button>`,
    );
  }

  // —— Fusebox ——
  private openFuseboxUI(): void {
    this.mode = 'examine';
    this.examineKind = 'fusebox';
    this.input.exitPointerLock();
    this.audio.playClick();
    this.renderFuseboxUI();
  }

  private renderFuseboxUI(): void {
    const fuseOn = this.puzzles.has('fuse_installed');
    const power = this.puzzles.has('power_restored');
    const pressureOk = this.puzzles.has('pressure_ok');
    const wires = this.puzzles.wires;
    const labels = ['青端子', '黄端子', '赤端子'];
    const seq = this.puzzles.wireSequence
      .map((i) => labels[i])
      .join(' → ') || '（未接続）';
    const wireBtns = labels
      .map(
        (lab, i) =>
          `<button class="wire-btn ${wires[i] ? 'on' : ''}" data-action="wire" data-payload="${i}">${lab}: ${wires[i] ? '接続済' : '未接続'}</button>`,
      )
      .join('');

    let body = '配電盤。圧力・ヒューズ・配線の三つが揃って初めて通電する。';
    if (!pressureOk) body = '警告灯が赤い。圧力計が適正でないと通電しない。';
    else if (power) body = '電力は復旧した。隠し通路が開いた。';
    else if (fuseOn) body = `ヒューズ装着済。配線順: ${seq}\n配線図どおり 青→黄→赤。`;

    this.ui.openExamine(
      '配電盤',
      body,
      power
        ? ''
        : `${
            fuseOn
              ? ''
              : `<button class="btn primary" data-action="fuse" data-payload="install">ヒューズを装着</button>`
          }
          <div class="row-btns" style="margin-top:0.6rem;flex-direction:column">${wireBtns}</div>
          <button class="btn" data-action="wire" data-payload="check">結線を確定</button>
          <button class="btn ghost" data-action="wire" data-payload="reset">配線リセット</button>`,
    );
  }

  // —— Brazier ——
  private handleBrazier(useMode: boolean): void {
    if (this.puzzles.has('lamp_lit')) {
      this.openExamineText('石の火床', 'ランタンの炎が揺れている。鏡に光が届き始めた。');
      return;
    }
    if (!this.inventory.has('oil_lantern')) {
      this.openExamineText('石の火床', '冷たい灰だけ。オイルランタンがあれば灯せる。');
      return;
    }
    void useMode;
    this.inventory.remove('oil_lantern');
    this.ui.refreshInventory(this.inventory);
    this.puzzles.set('lamp_lit');
    this.world.setBrazierLit(true);
    this.world.lighting.setHiddenLit(true);
    this.audio.playFire();
    this.audio.playSuccess();
    this.refreshObjectives();
    this.ui.openExamine(
      '石の火床',
      'ランタンを据えると、火床に温かな光が戻った。\n三面鏡がほのかに輝いている。',
    );
    this.mode = 'examine';
    this.input.exitPointerLock();
  }

  // —— Mirrors ——
  private openMirrorsUI(): void {
    if (this.puzzles.has('mirrors_done')) {
      this.openExamineText('三面鏡', '光は紋章へ集まり、数字 729 が浮かんでいる。');
      return;
    }
    if (!this.puzzles.has('lamp_lit')) {
      this.openExamineText(
        '三面鏡',
        '鏡は暗い。火床に灯りがなければ、光を導けない。',
      );
      return;
    }
    this.mode = 'examine';
    this.examineKind = 'mirrors';
    this.input.exitPointerLock();
    this.audio.playClick();
    this.renderMirrorsUI();
  }

  private renderMirrorsUI(): void {
    const m = this.puzzles.mirrors;
    const angles = (n: number) => `${n * 90}°`;
    this.ui.openExamine(
      '三面鏡',
      '各鏡を回転させ、光を紋章へ集めよ。\n（木箱の刻印がヒント）',
      `<div class="row-btns" style="flex-direction:column;gap:0.5rem">
        <button class="mirror-btn" data-action="mirror" data-payload="0">左: ${angles(m[0])} （回す）</button>
        <button class="mirror-btn" data-action="mirror" data-payload="1">中央: ${angles(m[1])} （回す）</button>
        <button class="mirror-btn" data-action="mirror" data-payload="2">右: ${angles(m[2])} （回す）</button>
      </div>
      <button class="btn primary" data-action="mirror" data-payload="check">光を放つ</button>`,
    );
    this.world.build.refs.mirrors.forEach((obj, i) => {
      obj.rotation.y = ((m[i] * Math.PI) / 2) * 0.15;
    });
  }

  // —— Exit ——
  private openExitUI(): void {
    if (this.puzzles.has('escaped')) return;
    this.mode = 'examine';
    this.examineKind = 'exit';
    this.exitInput = '';
    this.input.exitPointerLock();
    this.audio.playClick();
    this.renderExitUI();
  }

  private renderExitUI(): void {
    const keys = '123456789C0✓';
    const pad = keys
      .split('')
      .map((k) => {
        const payload = k === '✓' ? 'ok' : k === 'C' ? 'clear' : k;
        return `<button data-action="exit" data-payload="${payload}">${k}</button>`;
      })
      .join('');
    this.ui.openExamine(
      '玄関の大扉',
      'マスターキーを使うか、テンキーにコードを入力できる。',
      `<button class="btn primary" data-action="exit" data-payload="key">マスターキーを使う</button>
      <p style="margin:0.8rem 0 0.4rem;color:var(--muted)">— または テンキー —</p>
      <div class="display-code">${(this.exitInput + '___').slice(0, 3)}</div>
      <div class="numpad">${pad}</div>`,
    );
  }

  // —— Examine actions ——
  private handleExamineAction(action: string, payload?: string): void {
    if (action === 'clock') {
      if (payload === 'h+') this.clockHour = (this.clockHour % 12) + 1;
      if (payload === 'h-') this.clockHour = this.clockHour <= 1 ? 12 : this.clockHour - 1;
      if (payload === 'm+') this.clockMinute = (this.clockMinute + 5) % 60;
      if (payload === 'm-') this.clockMinute = (this.clockMinute + 55) % 60;
      if (payload === 'set') {
        this.world.setClock(this.clockHour, this.clockMinute);
        this.puzzles.clockHour = this.clockHour;
        this.puzzles.clockMinute = this.clockMinute;
        if (this.clockHour === PUZZLES.clockHour && this.clockMinute === PUZZLES.clockMinute) {
          this.puzzles.set('clock_done');
          this.audio.playSuccess();
          this.ui.toast('カチリ… ロックが外れた音');
          this.refreshObjectives();
          const d = this.interaction.get('living_drawer');
          if (d) d.locked = false;
        } else {
          this.audio.playError();
          this.ui.toast('何も起きない…');
        }
      } else {
        this.world.setClock(this.clockHour, this.clockMinute);
        this.audio.playClick();
      }
      this.renderClockUI();
      return;
    }

    if (action === 'shelf') {
      if (payload === 'reset') {
        this.puzzles.shelf = ['yellow', 'red', 'green', 'blue'];
        this.audio.playClick();
        this.renderBookshelfUI();
        return;
      }
      if (payload === 'check') {
        if (this.puzzles.isShelfCorrect()) {
          this.puzzles.set('shelf_done');
          this.audio.playSuccess();
          this.ui.toast('本棚がかちりと収まった');
          this.refreshObjectives();
          this.renderBookshelfUI();
        } else {
          this.audio.playError();
          this.ui.toast('まだ違う並びのようだ');
        }
        return;
      }
      const i = Number(payload);
      if (!Number.isNaN(i) && i < this.puzzles.shelf.length - 1) {
        const s = this.puzzles.shelf;
        const tmp = s[i];
        s[i] = s[i + 1];
        s[i + 1] = tmp;
        this.audio.playClick();
        this.renderBookshelfUI();
      } else if (i === this.puzzles.shelf.length - 1) {
        // 最後は先頭と回転
        const s = this.puzzles.shelf;
        const last = s.pop()!;
        s.unshift(last);
        this.audio.playClick();
        this.renderBookshelfUI();
      }
      return;
    }

    if (action === 'safe') {
      if (payload === 'clear') this.safeInput = '';
      else if (payload === 'ok') {
        if (this.safeInput === PUZZLES.safeCode) {
          this.puzzles.set('safe_opened');
          this.world.openSafe();
          this.audio.playSuccess();
          this.audio.playDoor();
          this.giveItem('basement_key');
          this.giveItem('fuse');
          this.refreshObjectives();
          this.ui.openExamine(
            '金庫',
            '錠が開いた。\n鉄の鍵とヒューズを手に入れた。\n次は《旋律》の部屋——ダイニングを探そう。',
          );
        } else {
          this.audio.playError();
          this.safeInput = '';
          this.ui.toast('違うようだ');
          this.renderSafeUI();
        }
      } else if (payload && this.safeInput.length < 4) {
        this.safeInput += payload;
        this.audio.playClick();
        this.renderSafeUI();
      }
      return;
    }

    if (action === 'piano') {
      if (payload === 'reset') {
        this.puzzles.pianoBuffer = [];
        this.audio.playClick();
        this.renderPianoUI();
        return;
      }
      if (!payload) return;
      this.audio.playPianoNote(payload);
      this.puzzles.pianoBuffer.push(payload);
      const target = PUZZLES.pianoMelody as unknown as string[];
      const buf = this.puzzles.pianoBuffer;
      // 途中不一致でリセット
      for (let i = 0; i < buf.length; i++) {
        if (buf[i] !== target[i]) {
          this.audio.playError();
          this.ui.toast('音を外した…');
          this.puzzles.pianoBuffer = [];
          this.renderPianoUI();
          return;
        }
      }
      if (buf.length === target.length) {
        this.puzzles.set('piano_done');
        this.audio.playSuccess();
        this.giveItem('silver_key');
        this.refreshObjectives();
        this.ui.openExamine(
          'ピアノ',
          'ド・ミ・ソ——正しい旋律だ。\n鍵盤下の小箱が開き、銀の鍵が出てきた。',
        );
      } else {
        this.renderPianoUI();
      }
      return;
    }

    if (action === 'valve') {
      if (payload === 'reset') {
        this.puzzles.valveSequence = [];
        this.audio.playClick();
        this.renderValvesUI();
        return;
      }
      if (!payload) return;
      this.puzzles.valveSequence.push(payload);
      this.audio.playClick();
      const order = PUZZLES.valveOrder as unknown as string[];
      const seq = this.puzzles.valveSequence;
      for (let i = 0; i < seq.length; i++) {
        if (seq[i] !== order[i]) {
          this.audio.playError();
          this.ui.toast('順序が違う…バルブが戻った');
          this.puzzles.valveSequence = [];
          this.renderValvesUI();
          return;
        }
      }
      if (seq.length === order.length) {
        this.puzzles.set('valves_done');
        this.audio.playSuccess();
        this.giveItem('oil_lantern');
        this.refreshObjectives();
        this.ui.openExamine(
          'バルブ',
          '正しい順序だ。配管から油が流れ、傍らのランタンに注がれた。\nオイルランタンを手に入れた。',
        );
      } else {
        this.renderValvesUI();
      }
      return;
    }

    if (action === 'pressure') {
      if (payload === '+') {
        this.puzzles.pressure = Math.min(12, this.puzzles.pressure + 1);
        this.audio.playClick();
        this.renderPressureUI();
        return;
      }
      if (payload === '-') {
        this.puzzles.pressure = Math.max(0, this.puzzles.pressure - 1);
        this.audio.playClick();
        this.renderPressureUI();
        return;
      }
      if (payload === 'set') {
        if (this.puzzles.pressure === PUZZLES.pressureTarget) {
          this.puzzles.set('pressure_ok');
          this.audio.playSuccess();
          this.ui.toast('圧力が安定した');
          this.refreshObjectives();
          this.ui.openExamine('圧力計', `針は ${PUZZLES.pressureTarget} で止まった。配電盤の警告灯が消えた。`);
        } else {
          this.audio.playError();
          this.ui.toast('まだ不安定だ…');
          this.renderPressureUI();
        }
      }
      return;
    }

    if (action === 'fuse') {
      if (!this.inventory.has('fuse')) {
        this.audio.playError();
        this.ui.toast('ヒューズを持っていない');
        return;
      }
      if (!this.puzzles.has('pressure_ok')) {
        this.audio.playError();
        this.ui.toast('圧力が不安定で装着できない');
        return;
      }
      this.puzzles.set('fuse_installed');
      this.inventory.remove('fuse');
      this.ui.refreshInventory(this.inventory);
      this.world.setFuseInstalled(true);
      this.audio.playPower();
      this.ui.toast('ヒューズを装着した');
      this.renderFuseboxUI();
      return;
    }

    if (action === 'wire') {
      if (payload === 'reset') {
        this.puzzles.wires = [false, false, false];
        this.puzzles.wireSequence = [];
        this.audio.playClick();
        this.renderFuseboxUI();
        return;
      }
      if (payload === 'check') {
        if (!this.puzzles.has('fuse_installed')) {
          this.audio.playError();
          this.ui.toast('先にヒューズが必要だ');
          return;
        }
        if (!this.puzzles.has('pressure_ok')) {
          this.audio.playError();
          this.ui.toast('圧力が足りない');
          return;
        }
        const target = PUZZLES.wireOrder as unknown as number[];
        const ok =
          this.puzzles.wireSequence.length === 3 &&
          this.puzzles.wireSequence.every((v, i) => v === target[i]) &&
          this.puzzles.wires.every(Boolean);
        if (ok) {
          this.puzzles.set('power_restored');
          this.puzzles.set('hidden_unlocked');
          this.world.lighting.setBasementPower(true);
          this.world.openHiddenWall();
          this.removeColliderTag('hidden_wall');
          this.audio.playPower();
          this.audio.playSuccess();
          this.ui.toast('電力復旧！ 壁が動いた');
          this.refreshObjectives();
          this.ui.openExamine(
            '配電盤',
            '電灯が点き、壁がスライドして開いた。\nランタンを持って隠し部屋へ。',
          );
        } else {
          this.audio.playError();
          this.ui.toast('順序か接続が違う');
          this.puzzles.wires = [false, false, false];
          this.puzzles.wireSequence = [];
          this.renderFuseboxUI();
        }
        return;
      }
      const idx = Number(payload);
      if (!Number.isNaN(idx) && !this.puzzles.wires[idx]) {
        this.puzzles.wires[idx] = true;
        this.puzzles.wireSequence.push(idx);
        this.audio.playClick();
        this.renderFuseboxUI();
      }
      return;
    }

    if (action === 'mirror') {
      if (payload === 'check') {
        const t = PUZZLES.mirrorTargets;
        const ok = this.puzzles.mirrors.every((v, i) => v === t[i]);
        if (ok) {
          this.puzzles.set('mirrors_done');
          this.world.setEmblemLit(true);
          this.audio.playSuccess();
          this.giveItem('master_key');
          this.refreshObjectives();
          const emb = this.interaction.get('emblem');
          if (emb) {
            emb.body = '紋章が輝き、数字が浮かんでいる。\n\n【 7 2 9 】';
          }
          this.ui.openExamine(
            '三面鏡',
            '光が紋章を貫き、数字【729】が浮かび上がった。\n台座にマスターキーを発見した。',
          );
        } else {
          this.audio.playError();
          this.ui.toast('光が逸れた…');
        }
        return;
      }
      const idx = Number(payload);
      if (!Number.isNaN(idx)) {
        this.puzzles.mirrors[idx] = (this.puzzles.mirrors[idx] + 1) % 4;
        this.audio.playClick();
        this.renderMirrorsUI();
      }
      return;
    }

    if (action === 'exit') {
      if (payload === 'key') {
        if (this.inventory.has('master_key')) this.doEscape();
        else {
          this.audio.playError();
          this.ui.toast('マスターキーを持っていない');
        }
        return;
      }
      if (payload === 'clear') {
        this.exitInput = '';
        this.renderExitUI();
        return;
      }
      if (payload === 'ok') {
        if (this.exitInput === PUZZLES.exitCode) this.doEscape();
        else {
          this.audio.playError();
          this.exitInput = '';
          this.ui.toast('コードが違う');
          this.renderExitUI();
        }
        return;
      }
      if (payload && this.exitInput.length < 3) {
        this.exitInput += payload;
        this.audio.playClick();
        this.renderExitUI();
      }
    }
  }

  private handleLivingDrawer(): void {
    if (!this.puzzles.has('clock_done')) {
      this.openExamineText('サイドボードの引き出し', '途中で止まる。時計と連動しているようだ。');
      return;
    }
    if (this.inventory.has('copper_key')) {
      this.openExamineText('引き出し', 'すでに空だ。');
      return;
    }
    this.world.openDrawer('living');
    this.audio.playDrawer();
    this.giveItem('copper_key');
    this.ui.openExamine('引き出し', '奥に銅の鍵がしまわれていた。');
    this.mode = 'examine';
    this.input.exitPointerLock();
  }

  private handleLocked(
    target: { id: string; title: string; body?: string; requires?: ItemId; locked?: boolean },
    useMode: boolean,
  ): void {
    if (target.id === 'study_desk') {
      if (this.puzzles.has('desk_opened')) {
        this.openExamineText('書斎の机', '引き出しは開いている。');
        return;
      }
      if (this.inventory.has('copper_key')) {
        this.puzzles.set('desk_opened');
        this.world.openDrawer('desk');
        this.audio.playDrawer();
        this.giveItem('cipher_note');
        this.giveItem('blank_paper');
        this.refreshObjectives();
        this.ui.openExamine(
          '机の引き出し',
          '銅の鍵が合った。\n\n・暗号メモ「絵画の数字と、時を刻む分を繋げ。ただし金庫は《言葉》を先に知れ」\n・白紙の羊皮紙（熱反応紙？）\n\nを手に入れた。',
        );
        this.mode = 'examine';
        this.input.exitPointerLock();
        return;
      }
      void useMode;
      this.openExamineText(target.title, target.body ?? '鍵がかかっている。');
      return;
    }

    if (target.id === 'basement_door') {
      if (this.puzzles.has('basement_unlocked') || this.openDoors.has('basement')) {
        this.openExamineText('地下室への扉', '扉は開いている。');
        return;
      }
      if (this.inventory.has('basement_key')) {
        this.puzzles.set('basement_unlocked');
        this.openDoors.add('basement');
        this.world.openBasementDoor();
        this.removeColliderTag('basement_door');
        this.audio.playDoor();
        this.ui.toast('地下室の扉を開けた');
        this.closeExamine();
        return;
      }
      this.openExamineText(target.title, '鉄の鍵穴がある。合鍵が必要だ。');
    }
  }

  private doEscape(): void {
    this.puzzles.set('escaped');
    this.world.openExitDoor();
    this.removeColliderTag('exit_door');
    this.audio.playDoor();
    this.audio.playClear();
    this.mode = 'clear';
    this.input.exitPointerLock();
    this.ui.showClear(this.playTime);
  }

  private giveItem(id: ItemId): void {
    if (this.inventory.add(id)) {
      this.audio.playPickup();
      this.ui.showItemGet(id);
      this.ui.refreshInventory(this.inventory);
    }
  }

  private removeColliderTag(tag: string): void {
    this.player.colliders = this.player.colliders.filter((c) => c.tag !== tag);
  }

  private onResize = (): void => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h);
    this.player?.resize(w / h);
    this.world?.resize(w, h);
  };

  private delay(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
  }
}
